import React, { useCallback, useEffect, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import ListItemText from '@mui/material/ListItemText'
import LayersIcon from '@mui/icons-material/Layers'
import CheckIcon from '@mui/icons-material/Check'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { applyHereMapBaseView, getPreferredMapBaseView } from './mapView'
import { HereMapBaseView, HereMapDefaultLayers } from './types'
import maplibregl from 'maplibre-gl'
import { getMapLibreStyle } from './utils/maplibreStyles'
import { preservePinLayers } from './HereMapNew/hooks/useMapLibreMarkers'

type Props = {
  hereMap: H.Map | undefined
  defaultLayers?: HereMapDefaultLayers
  mlMap?: maplibregl.Map | null
}

export function LayerControls({ hereMap, defaultLayers, mlMap }: Props) {
  const dispatch = useAppDispatch()
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const preferredMapBaseView = getPreferredMapBaseView(userGeneralPreferences)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedLayer, setSelectedLayer] =
    useState<HereMapBaseView>(preferredMapBaseView)
  const [trafficLayer, setTrafficLayer] = useState<H.map.layer.Layer | null>(
    null,
  )
  const open = Boolean(anchorEl)

  useEffect(() => {
    setSelectedLayer(preferredMapBaseView)
  }, [preferredMapBaseView])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLayerChange = useCallback(
    (layerType: HereMapBaseView) => {
      if (mlMap) {
        // Carry the pin sources/layers across the diff so the pins don't flash
        // out and back in while the base map changes (SH-909). The preference
        // dispatch below also triggers a setStyle in useMapLibreInstance — that
        // one uses the same transform, so neither call tears the pins down.
        mlMap.setStyle(getMapLibreStyle(layerType), {
          diff: true,
          transformStyle: preservePinLayers,
        })
      } else if (hereMap && defaultLayers) {
        applyHereMapBaseView(hereMap, defaultLayers, layerType)
      } else {
        handleClose()
        return
      }

      setSelectedLayer(layerType)
      handleClose()

      // Persist the selection so it is restored on reopen / other devices
      // (SH-566). Must run for the MapLibre path too — previously only the
      // HERE branch saved it, so the choice was lost after the migration.
      setTimeout(() => {
        dispatch(
          saveGeneralPreferences({
            type: IUserPreferenceGeneralListItemType.MapViewBaseLayer,
            value: layerType,
          }),
        )
      }, 0)
    },
    [dispatch, hereMap, defaultLayers, mlMap],
  )

  const handleTrafficConditions = useCallback(() => {
    if (!hereMap || !defaultLayers) return

    if (trafficLayer) {
      hereMap.removeLayer(trafficLayer)
      setTrafficLayer(null)
    } else {
      let layer: H.map.layer.Layer

      switch (selectedLayer) {
        case 'terrain':
          layer = defaultLayers.vector.traffic.topo
          break
        case 'satellite':
          layer = defaultLayers.hybrid.day.traffic
          break
        case 'vector':
        default:
          layer = defaultLayers.vector.traffic.map
          break
      }

      hereMap.addLayer(layer)
      setTrafficLayer(layer)
    }
    handleClose()
  }, [hereMap, defaultLayers, selectedLayer, trafficLayer])

  const layerOptions = [
    { key: 'vector', label: 'Map', description: 'Street map view' },
    { key: 'satellite', label: 'Satellite', description: 'Satellite imagery' },
    { key: 'terrain', label: 'Terrain', description: 'Terrain view' },
  ] as const

  return (
    <Box>
      <Paper elevation={4}>
        <IconButton sx={{ p: 1 }} onClick={handleClick}>
          <LayersIcon fontSize="small" />
        </IconButton>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              transform: 'translateX(-16px) translateY(-12px) !important',
              borderRadius: 2,
              minWidth: 180,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          },
        }}
      >
        {layerOptions.map((option) => (
          <MenuItem
            key={option.key}
            onClick={() => handleLayerChange(option.key)}
            sx={{
              py: 1.5,
              px: 2,
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ flexGrow: 1 }}>
                <ListItemText
                  primary={option.label}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: selectedLayer === option.key ? 600 : 400,
                    color: '#333',
                  }}
                />
              </Box>
              {selectedLayer === option.key && (
                <CheckIcon
                  sx={{ fontSize: 16, color: '#1976d2', ml: 1 }}
                  data-test-id={`mapLayerSelectedIcon-${option.key}`}
                />
              )}
            </Box>
          </MenuItem>
        ))}
        {!mlMap && <Divider />}
        {!mlMap && (
          <MenuItem onClick={handleTrafficConditions}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ flexGrow: 1 }}>
                <ListItemText
                  primary="Traffic Conditions"
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: trafficLayer ? 600 : 400,
                    color: '#333',
                  }}
                />
              </Box>
              {trafficLayer && (
                <CheckIcon
                  sx={{ fontSize: 16, color: '#1976d2', ml: 1 }}
                  data-test-id="mapTrafficConditionsSelectedIcon"
                />
              )}
            </Box>
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
